import { delay } from '@passionware/platform-js';
import { revealVertically, verticalScrollParent } from './revealVertically';
import { whenAnimationsSettle } from './whenAnimationsSettle';

/** Frames in a row the layout has to stay the same to count as settled. */
const STILL_FRAMES = 3;

/**
 * Settles once `element` and the scroll area around it have stopped
 * changing — a section or a group around it growing open, the element
 * itself resizing — so a scroll that follows lands where it ends up. Waits
 * for the CSS animations in the scroll area (`whenAnimationsSettle`), then
 * for heights that Motion animates frame by frame, which `getAnimations`
 * doesn't see: the element's box and the scroll area's height have to stay
 * the same for a few frames in a row. Gives up waiting after `maxMs`.
 */
export async function whenLayoutSettles(
  element: Element,
  maxMs = 1500
): Promise<void> {
  const start = performance.now();
  const scroller =
    verticalScrollParent(element) ?? document.scrollingElement ?? element;
  await Promise.race([whenAnimationsSettle(scroller), delay(maxMs)]);
  const layout = () => {
    const { top, height } = element.getBoundingClientRect();
    return `${top}/${height}/${scroller.scrollHeight}`;
  };
  return new Promise(resolve => {
    const check = (last: string, stillFrames: number) => {
      const current = layout();
      const still = current === last ? stillFrames + 1 : 0;
      if (still >= STILL_FRAMES || performance.now() - start > maxMs) {
        resolve();
        return;
      }
      requestAnimationFrame(() => check(current, still));
    };
    requestAnimationFrame(() => check('', 0));
  });
}

/**
 * Shows `element` (`revealVertically`) once the layout around it has
 * settled (`whenLayoutSettles`); nothing when it has left the page by then.
 * Taller than the view, it shows `inner` instead when given: the part in
 * use inside a long card.
 */
export async function revealWhenSettled(
  element: Element,
  behavior: ScrollBehavior,
  inner: Element | null = null
): Promise<void> {
  await whenLayoutSettles(element);
  if (!element.isConnected) return;
  const view =
    verticalScrollParent(element)?.clientHeight ?? window.innerHeight;
  const tall = element.getBoundingClientRect().height > view;
  revealVertically(tall && inner?.isConnected ? inner : element, behavior);
}
